import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useProgram } from '../../context/ProgramContext';
import { useAudio } from '../../context/AudioContext';
import apiClient from '../../utils/api';
import AdminBanner from '../AdminBanner';
import TrialBanner from '../TrialBanner';
import EnergyVisualizer from '../EnergyVisualizer';
import ProxyCardsWidget from '../ProxyCardsWidget';
import RecommendationWidget from '../RecommendationWidget';
import PractitionerRecommendationWidget from '../Practitioner/PractitionerRecommendationWidget';
import MoodDialog from '../Chatbot/MoodDialog';
import DailyCheckinDialog from '../Chatbot/DailyCheckinDialog';
import PractitionerAccessDialog from '../Practitioner/PractitionerAccessDialog';
import { AlertDialog } from '../CustomDialog';
import AppHeader from '../Header/AppHeader';
import FrequencyGrid from './FrequencyGrid';
import FrequencyCircle from './FrequencyCircle';
import './UserDashboard.css';

function UserDashboard() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { runningProgram, currentStep, timeRemaining, stopProgram } = useProgram();
  const audio = useAudio();
  const navigate = useNavigate();
  const isGerman = i18n.language === 'de';
  const [frequencies, setFrequencies] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layoutMode, setLayoutMode] = useState('combined'); // new default: combined view
  const [showProgramWarning, setShowProgramWarning] = useState(false);
  const [showMoodDialog, setShowMoodDialog] = useState(false);
  const [showDailyCheckin, setShowDailyCheckin] = useState(false);
  const [deactivatedFrequency, setDeactivatedFrequency] = useState(null);
  const [pendingFrequencyId, setPendingFrequencyId] = useState(null);
  const [moodOnSwitchEnabled, setMoodOnSwitchEnabled] = useState(true);
  const [alertDialog, setAlertDialog] = useState(null);
  const warningTimeoutRef = useRef(null);
  const checkinCheckedRef = useRef(false);

  // Use global audio context state
  const activeFrequency = audio.activeFrequency;
  const audioEnabled = audio.audioEnabled;
  const setActiveFrequency = audio.setActiveFrequency;
  const toggleAudio = audio.toggleAudio;
  const isPlaying = audio.isPlaying;
  const volume = audio.volume;
  const setVolume = audio.setVolume;
  const currentTrackIndex = audio.currentTrackIndex;
  const audioTracks = audio.audioTracks;
  const togglePlayPause = audio.togglePlayPause;
  const handleNext = audio.handleNext;
  const handlePrevious = audio.handlePrevious;

  // Program name translations
  const programTranslations = {
    'Morgen-Energie': 'Morning Energy',
    'Abend-Entspannung': 'Evening Relaxation',
    'Fokus-Flow': 'Focus Flow',
    'Tiefen-Heilung': 'Deep Healing',
    'Stress-Reset': 'Stress Reset',
    'Kreativ-Boost': 'Creative Boost',
    'Schlaf-Vorbereitung': 'Sleep Preparation',
    'Energie-Aktivierung': 'Energy Activation'
  };

  const translateProgramName = (name) => {
    if (isGerman) return name;
    return programTranslations[name] || name;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Frequency translations for English
  const frequencyTranslations = {
    'ZENTA.sleep': {
      en: 'When the day won\'t let go and your mind can\'t rest: This frequency gently guides you into sleep.'
    },
    'ZENTA.energy': {
      en: 'Feel your inner fire reawaken. This frequency activates your system and lifts your mood.'
    },
    'ZENTA.calm': {
      en: 'When everything is too much: This frequency brings you back to stillness and releases tension.'
    },
    'ZENTA.heart': {
      en: 'Open your heart – for yourself, for others, for life. This frequency nurtures your inner child.'
    },
    'ZENTA.focus': {
      en: 'For clarity of mind and presence in the moment. This frequency focuses your energy.'
    },
    'ZENTA.align': {
      en: 'This frequency helps you realign your energetic system. For balance and grounding.'
    },
    'ZENTA.protect': {
      en: 'This frequency strengthens your energetic boundaries and helps you stay centered.'
    },
    'ZENTA.moon': {
      en: 'This frequency supports you in phases of change and helps you let go.'
    },
    'ZENTA.intuit': {
      en: 'This frequency opens space for subtle perception and inner guidance.'
    },
    'ZENTA.body': {
      en: 'This frequency strengthens your connection to your body and regulates body awareness.'
    },
    'ZENTA.nature': {
      en: 'This frequency strengthens the connection to natural life fields.'
    },
    'ZENTA.abund': {
      en: 'This frequency releases energetic blocks around abundance and opens you to receiving.'
    }
  };

  // Translate frequencies if English
  const translateFrequencies = (freqs) => {
    if (isGerman) return freqs;
    return freqs.map(freq => ({
      ...freq,
      description: frequencyTranslations[freq.name]?.en || freq.description
    }));
  };

  useEffect(() => {
    loadData();
  }, []);

  // Check for daily check-in on mount
  useEffect(() => {
    const checkDailyCheckin = async () => {
      if (checkinCheckedRef.current) return;
      checkinCheckedRef.current = true;
      
      try {
        const response = await apiClient.shouldShowDailyCheckin();
        if (response.should_show) {
          // Small delay for better UX
          setTimeout(() => {
            setShowDailyCheckin(true);
          }, 1500);
        }
      } catch (error) {
        console.error('Error checking daily checkin:', error);
      }
    };
    
    checkDailyCheckin();
  }, []);

  // Reload active frequency when program changes step
  useEffect(() => {
    if (runningProgram && currentStep >= 0) {
      // Small delay to ensure backend has updated
      const timer = setTimeout(() => {
        reloadActiveFrequency();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  // Cleanup warning timeout on unmount
  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, []);

  const reloadActiveFrequency = async () => {
    try {
      const activeData = await apiClient.getActiveFrequency();
      if (activeData) {
        // Only reload if frequency actually changed (to prevent audio reload)
        if (!activeFrequency || activeFrequency.id !== activeData.id) {
          console.log('[Dashboard] Program step - frequency changed, updating');
          // Get full frequency data with audio files
          const freqData = await apiClient.getFrequencies();
          const fullFreq = freqData.find(f => f.id === activeData.id);
          setActiveFrequency(fullFreq || activeData);
        } else {
          console.log('[Dashboard] Program step - same frequency, skipping update');
        }
      } else {
        if (activeFrequency) {
          console.log('[Dashboard] Program step - clearing frequency');
          setActiveFrequency(null);
        }
      }
    } catch (error) {
      console.error('Error reloading active frequency:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [freqData, activeData, favData, prefsData, subStatus] = await Promise.all([
        apiClient.getFrequencies(),
        apiClient.getActiveFrequency(),
        apiClient.getFavorites(),
        apiClient.getPreferences(),
        apiClient.getSubscriptionStatus(),
      ]);
      setFrequencies(translateFrequencies(freqData));
      
      // CRITICAL: Only sync active frequency if it actually changed (to prevent audio reload)
      if (activeData) {
        const fullFreq = freqData.find(f => f.id === activeData.id);
        // Only set if frequency changed (compare IDs)
        if (!activeFrequency || activeFrequency.id !== activeData.id) {
          console.log('[Dashboard] Active frequency changed, updating context');
          setActiveFrequency(fullFreq || activeData);
        } else {
          console.log('[Dashboard] Active frequency unchanged, skipping context update');
        }
      } else if (activeFrequency) {
        // Only clear if there was an active frequency before
        console.log('[Dashboard] Clearing active frequency');
        setActiveFrequency(null);
      }
      
      setFavorites(favData.map((f) => f.id));
      
      // Set audio enabled in global context if it was previously enabled
      if (prefsData.audio_enabled && !audioEnabled) {
        toggleAudio();
      }
      
      setPreferences(prefsData);
      setSubscriptionStatus(subStatus);
      
      // Load mood-on-switch setting (default: true)
      const moodSetting = prefsData.mood_on_switch_enabled;
      setMoodOnSwitchEnabled(moodSetting !== false);
      console.log('Mood-on-switch setting loaded:', moodSetting !== false);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (frequencyId) => {
    // Check if subscription is valid (trial or active subscription)
    if (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed) {
      setAlertDialog({
        type: 'warning',
        title: 'Testphase abgelaufen',
        message: 'Bitte upgraden, um Frequenzen weiter zu nutzen.'
      });
      return;
    }

    // Block if program is running
    if (runningProgram) {
      // Clear existing timeout if any
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      
      // Show warning
      setShowProgramWarning(true);
      
      // Set new timeout for 10 seconds
      warningTimeoutRef.current = setTimeout(() => {
        setShowProgramWarning(false);
      }, 10000);
      
      return;
    }

    try {
      // Check if we're deactivating or switching
      const isDeactivating = activeFrequency?.id === frequencyId;
      const isSwitching = activeFrequency && activeFrequency.id !== frequencyId;
      
      console.log('Activate clicked:', {
        frequencyId,
        activeFrequencyId: activeFrequency?.id,
        isDeactivating,
        isSwitching,
        moodOnSwitchEnabled: moodOnSwitchEnabled,
        hasActiveFrequency: !!activeFrequency
      });
      
      // Show mood dialog if:
      // 1. Deactivating completely, OR
      // 2. Switching to another frequency AND user has enabled mood-on-switch
      if ((isDeactivating || (isSwitching && moodOnSwitchEnabled)) && activeFrequency) {
        console.log('Showing mood dialog for:', activeFrequency.name);
        // Store the frequency being deactivated/switched from
        setDeactivatedFrequency(activeFrequency);
        setPendingFrequencyId(isDeactivating ? null : frequencyId);
        setShowMoodDialog(true);
        return; // Don't continue - wait for mood dialog
      }
      
      console.log('Proceeding without mood dialog');
      // If no mood dialog needed, proceed with activation
      const newActiveId = isDeactivating ? null : frequencyId;
      const result = await apiClient.setActiveFrequency(newActiveId);
      
      // Reload frequencies to get updated audio_files with fallback
      const freqData = await apiClient.getFrequencies();
      setFrequencies(translateFrequencies(freqData));
      
      // Find and set the active frequency with full data including fallback audio
      if (result) {
        const fullFreq = freqData.find(f => f.id === result.id);
        setActiveFrequency(fullFreq || result);
      } else {
        setActiveFrequency(null);
      }
    } catch (error) {
      console.error('Error setting active frequency:', error);
    }
  };

  const handleMoodDialogClose = async (saved) => {
    setShowMoodDialog(false);
    
    // After mood dialog, activate the pending frequency
    if (pendingFrequencyId !== null) {
      try {
        const result = await apiClient.setActiveFrequency(pendingFrequencyId);
        const freqData = await apiClient.getFrequencies();
        setFrequencies(translateFrequencies(freqData));
        
        if (result) {
          const fullFreq = freqData.find(f => f.id === result.id);
          setActiveFrequency(fullFreq || result);
        } else {
          setActiveFrequency(null);
        }
      } catch (error) {
        console.error('Error activating pending frequency:', error);
      }
    } else {
      // Just deactivating, update state
      setActiveFrequency(null);
    }
    
    setDeactivatedFrequency(null);
    setPendingFrequencyId(null);
  };

  // Routine scheduler - runs after handleActivate is defined
  useEffect(() => {
    if (!preferences) return;

    const checkRoutine = () => {
      const now = new Date();
      const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      
      // Check morning routine
      if (
        preferences.morning_time === currentTime &&
        preferences.morning_frequency_id &&
        activeFrequency?.id !== preferences.morning_frequency_id
      ) {
        handleActivate(preferences.morning_frequency_id);
        
        if (preferences.reminders_enabled && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('🌅 ZENTA Morgen-Routine', {
            body: 'Deine Morgen-Frequenz wurde aktiviert',
          });
        }
      }

      // Check evening routine
      if (
        preferences.evening_time === currentTime &&
        preferences.evening_frequency_id &&
        activeFrequency?.id !== preferences.evening_frequency_id
      ) {
        handleActivate(preferences.evening_frequency_id);
        
        if (preferences.reminders_enabled && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('🌙 ZENTA Abend-Routine', {
            body: 'Deine Abend-Frequenz wurde aktiviert',
          });
        }
      }
    };

    // Request notification permission
    if (preferences.reminders_enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const interval = setInterval(checkRoutine, 60000);
    checkRoutine();

    return () => clearInterval(interval);
  }, [preferences, activeFrequency]);

  const handleToggleFavorite = async (frequencyId) => {
    try {
      if (favorites.includes(frequencyId)) {
        await apiClient.removeFromFavorites(frequencyId);
        setFavorites(favorites.filter((id) => id !== frequencyId));
      } else {
        await apiClient.addToFavorites(frequencyId);
        setFavorites([...favorites, frequencyId]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  const handleToggleAudio = async () => {
    toggleAudio(); // Use global audio toggle
    const newAudioEnabled = !audioEnabled;
    
    try {
      await apiClient.updatePreferences({ audio_enabled: newAudioEnabled });
    } catch (error) {
      console.error('Error updating preferences:', error);
      toggleAudio(); // Revert on error
    }
  };

  const handleLogout = () => {
    // Clear all audio on logout
    audio.clearAudio();
    logout();
    navigate('/login');
  };

  const handleGoToAdmin = () => {
    navigate('/admin');
  };

  const handleGoToHistory = () => {
    navigate('/history');
  };

  const handleGoToNotes = () => {
    navigate('/notes');
  };

  const handleGoToSettings = () => {
    navigate('/settings');
  };

  const handleGoToPrograms = () => {
    navigate('/programs');
  };

  const handleGoToChat = () => {
    navigate('/chat');
  };

  // Filter frequencies based on favorites toggle
  const displayedFrequencies = showOnlyFavorites
    ? frequencies.filter((freq) => favorites.includes(freq.id))
    : frequencies;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Lade Dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <AppHeader subscriptionStatus={subscriptionStatus} />
      <div className="dashboard-container">
        <PractitionerAccessDialog />
        <AdminBanner />
        <TrialBanner />
        <EnergyVisualizer
          color={activeFrequency?.color_hex}
          isActive={!!activeFrequency && (subscriptionStatus?.is_trial || subscriptionStatus?.is_subscribed)}
        />

      <main className="dashboard-main">
        <div className="welcome-message">
          <div className="welcome-header">
            <div>
              <h2 className="welcome-text">Hi {user.name || 'dort'} 👋</h2>
              <p className="welcome-subtitle">{isGerman ? 'Wähle deine Frequenz und lass die Energie fließen' : 'Choose your frequency and let the energy flow'}</p>
            </div>
            {subscriptionStatus && subscriptionStatus.status !== 'none' && (
              <div className="subscription-badge-container">
                <button 
                  onClick={() => navigate('/subscription/manage')}
                  className={`subscription-badge ${subscriptionStatus.tier || 'standard'}`}
                  data-testid="subscription-badge"
                >
                  <span className="badge-icon">
                    {subscriptionStatus.tier === 'premium' ? '💎' : subscriptionStatus.tier === 'trial' ? '🆓' : '⭐'}
                  </span>
                  <span className="badge-text">
                    {subscriptionStatus.tier === 'premium' ? 'Premium' : 
                     subscriptionStatus.tier === 'trial' ? 'Trial' : 'Standard'}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        <RecommendationWidget onActivate={handleActivate} />
        <PractitionerRecommendationWidget />
        
        {/* Navigation Center - Active Frequency + Program + Audio Controls */}
        <div className="navigation-center" data-testid="navigation-center">
          {/* Program Runner - only shown when program is active */}
          {runningProgram && (
            <div className="nav-program-section">
              <div className="nav-program-header">
                <span className="nav-section-label">{isGerman ? 'AKTIVES PROGRAMM' : 'ACTIVE PROGRAM'}</span>
                <button 
                  onClick={stopProgram} 
                  className="nav-program-stop" 
                  title={isGerman ? 'Beenden' : 'Stop'}
                >
                  ⏹️ {isGerman ? 'Beenden' : 'Stop'}
                </button>
              </div>
              <div className="nav-program-content">
                <div className="nav-program-info">
                  <span className="nav-program-icon">{runningProgram.icon}</span>
                  <span className="nav-program-name">{translateProgramName(runningProgram.name)}</span>
                </div>
                <div className="nav-program-step">
                  <div 
                    className="nav-step-freq-icon" 
                    style={{ color: runningProgram.steps[currentStep]?.frequency_color }}
                  >
                    {runningProgram.steps[currentStep]?.frequency_icon}
                  </div>
                  <div className="nav-step-info">
                    <span className="nav-step-name">{runningProgram.steps[currentStep]?.frequency_name}</span>
                    <span className="nav-step-progress">
                      {t('programs.step', { current: currentStep + 1, total: runningProgram.steps.length })}
                    </span>
                  </div>
                  <div className="nav-step-timer">{formatTime(timeRemaining)}</div>
                </div>
                <div className="nav-program-dots">
                  {runningProgram.steps.map((step, index) => (
                    <div
                      key={index}
                      className={`nav-step-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                      style={{
                        backgroundColor: index <= currentStep ? step.frequency_color : 'rgba(255,255,255,0.2)'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Active Frequency Section */}
          <div className="nav-frequency-section">
            <div className="nav-section-label">{isGerman ? 'AKTIVE FREQUENZ' : 'ACTIVE FREQUENCY'}</div>
            {activeFrequency ? (
              <div className="nav-freq-active">
                <div className="nav-freq-main">
                  <div 
                    className="nav-freq-badge"
                    style={{ 
                      backgroundColor: activeFrequency.color_hex,
                      boxShadow: `0 0 30px ${activeFrequency.color_hex}40`
                    }}
                  >
                    <span className="nav-freq-icon">{activeFrequency.icon}</span>
                    <span className="nav-freq-name">{activeFrequency.name}</span>
                  </div>
                  
                  {/* Audio Toggle */}
                  <div className="nav-audio-toggle">
                    <label className="audio-toggle-label">
                      <input
                        type="checkbox"
                        checked={audioEnabled}
                        onChange={handleToggleAudio}
                        data-testid="audio-toggle"
                      />
                      <span className="audio-toggle-slider"></span>
                      <span className="audio-toggle-text">
                        🔊 {audioEnabled ? t('common.audioOn') : t('common.audioOff')}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Integrated Audio Player - only when audio is enabled */}
                {audioEnabled && audioTracks && audioTracks.length > 0 && (
                  <div className="nav-audio-player">
                    <div className="nav-audio-info">
                      <span className="nav-audio-track">
                        Track {currentTrackIndex + 1} {isGerman ? 'von' : 'of'} {audioTracks.length}
                      </span>
                    </div>
                    <div className="nav-audio-controls">
                      <button
                        onClick={handlePrevious}
                        className="nav-audio-btn"
                        disabled={audioTracks.length <= 1}
                        title={isGerman ? 'Vorheriger Track' : 'Previous track'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
                        </svg>
                      </button>
                      <button
                        onClick={togglePlayPause}
                        className="nav-audio-play"
                        style={{ borderColor: activeFrequency.color_hex }}
                        title={isPlaying ? 'Pause' : (isGerman ? 'Abspielen' : 'Play')}
                      >
                        {isPlaying ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" rx="1" />
                            <rect x="14" y="4" width="4" height="16" rx="1" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                      </button>
                      <button
                        onClick={handleNext}
                        className="nav-audio-btn"
                        disabled={audioTracks.length <= 1}
                        title={isGerman ? 'Nächster Track' : 'Next track'}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="nav-freq-inactive">
                <div className="nav-freq-badge inactive">
                  <span className="nav-freq-icon">◯</span>
                  <span className="nav-freq-name">{isGerman ? 'Keine Frequenz aktiviert' : 'No frequency active'}</span>
                </div>
                <p className="nav-freq-hint">{isGerman ? 'Wähle eine Frequenz aus dem Kreis unten' : 'Select a frequency from the circle below'}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="dashboard-controls">
          <div className="favorites-filter">
            <button
              className={`filter-btn ${showOnlyFavorites ? 'active' : ''}`}
              onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
              data-testid="favorites-filter"
              title={showOnlyFavorites ? (isGerman ? 'Alle anzeigen' : 'Show all') : (isGerman ? 'Nur Favoriten anzeigen' : 'Show favorites only')}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill={showOnlyFavorites ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                <path d="M10 2l2.5 6.5L19 9.5l-5.5 4.5L15 20l-5-3.5L5 20l1.5-6L1 9.5l6.5-1L10 2z" />
              </svg>
              <span>{showOnlyFavorites ? (isGerman ? 'Alle anzeigen' : 'Show all') : (isGerman ? 'Nur Favoriten' : 'Favorites only')}</span>
            </button>
          </div>
        </div>

        {displayedFrequencies.length === 0 ? (
          <div className="empty-state">
            <p>{showOnlyFavorites ? t('dashboard.noFavorites') : t('dashboard.noFrequencies')}</p>
            <p className="empty-state-hint">
              {showOnlyFavorites ? t('dashboard.noFavoritesHint') : t('dashboard.contactAdmin')}
            </p>
          </div>
        ) : (
          <div className="frequencies-container combined-view">
            {/* Circle View - Always shown */}
            <FrequencyCircle
              frequencies={displayedFrequencies}
              activeFrequency={activeFrequency}
              favorites={favorites}
              onActivate={handleActivate}
              onToggleFavorite={handleToggleFavorite}
              isDisabled={!!runningProgram || (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed)}
            />
            
            {/* Grid View - Below circle */}
            <div className="frequency-grid-section">
              <h3 className="section-title">{isGerman ? 'Alle Frequenzen' : 'All Frequencies'}</h3>
              <FrequencyGrid
                frequencies={displayedFrequencies}
                activeFrequency={activeFrequency}
                favorites={favorites}
                onActivate={handleActivate}
                onToggleFavorite={handleToggleFavorite}
                isDisabled={!!runningProgram || (subscriptionStatus && !subscriptionStatus.is_trial && !subscriptionStatus.is_subscribed)}
              />
            </div>
          </div>
        )}

        <ProxyCardsWidget />
      </main>

      {showProgramWarning && (
        <div className="program-warning-overlay" data-testid="program-warning">
          <div className="warning-icon">⚠️</div>
          <div className="warning-content">
            <strong>{t('dashboard.programRunning')}</strong>
            <p>{t('dashboard.programRunningHint')}</p>
          </div>
        </div>
      )}

      {showMoodDialog && deactivatedFrequency && (
        <MoodDialog
          frequencyId={deactivatedFrequency.id}
          frequencyName={deactivatedFrequency.name}
          onClose={handleMoodDialogClose}
        />
      )}

      {showDailyCheckin && (
        <DailyCheckinDialog
          onClose={() => setShowDailyCheckin(false)}
        />
      )}

      {alertDialog && (
        <AlertDialog
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog(null)}
        />
      )}
      </div>
    </>
  );
}

export default UserDashboard;
